import { useCallback, useState } from 'react';

import {
  useWorkspacesPageAddMemberMutation,
  useWorkspacesPageRemoveAssignmentMutation,
  useWorkspacesPageRevokeAllMutation,
  useWorkspacesPageSetAssignmentsMutation,
} from '../../../generated';

export interface AddMemberInput {
  workspaceId: string;
  userId: string;
}

export interface AssignRoleInput {
  workspaceId: string;
  userId: string;
  locationId: string;
  roleId: string;
}

/** One place and the role held there, as picked in a form. */
export interface AssignmentDraft {
  locationId: string;
  roleId: string;
}

export interface AssignRolesInput {
  workspaceId: string;
  userId: string;
  assignments: AssignmentDraft[];
}

export interface RemoveRoleInput {
  workspaceId: string;
  userId: string;
  locationId: string;
  roleId: string;
  /** Acknowledges that dropping their last role removes them entirely. */
  allowRemoveFromWorkspace?: boolean;
}

export interface RevokeAllInput {
  workspaceId: string;
  userId: string;
}

export interface RemoveRoleOutcome {
  removed: boolean;
  /** True when the service refused because it was their last role. */
  wouldRemoveFromWorkspace: boolean;
}

export interface UseMemberActionsResult {
  addMember: (input: AddMemberInput) => Promise<boolean>;
  assignRole: (input: AssignRoleInput) => Promise<boolean>;
  /** Every pair in one transaction: they all apply, or none do. */
  assignRoles: (input: AssignRolesInput) => Promise<boolean>;
  removeRole: (input: RemoveRoleInput) => Promise<RemoveRoleOutcome>;
  revokeAll: (input: RevokeAllInput) => Promise<boolean>;
  isWorking: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Adding a member and granting them a role.
 *
 * Each failure the service can produce has its own type, so the message names
 * what actually went wrong — an already-claimed member, a role that does not
 * apply at that tier — rather than a generic error.
 */
export const useMemberActions = (
  onChanged: () => Promise<void> | void
): UseMemberActionsResult => {
  const [error, setError] = useState<string | null>(null);
  const [add, addState] = useWorkspacesPageAddMemberMutation();
  const [assign, assignState] = useWorkspacesPageSetAssignmentsMutation();
  const [remove, removeState] = useWorkspacesPageRemoveAssignmentMutation();
  const [revoke, revokeState] = useWorkspacesPageRevokeAllMutation();

  const addMember = useCallback(
    async ({ workspaceId, userId }: AddMemberInput): Promise<boolean> => {
      setError(null);

      const { data } = await add({
        variables: { input: { workspaceId, userId } },
      });
      const result = data?.teamMemberAdd;

      switch (result?.__typename) {
        // Deliberately does not report a change: a member holding no
        // assignment appears in no read, so the refresh belongs to whatever
        // grants them one.
        case 'TeamMemberAddResult':
          return true;
        case 'TeamMemberAlreadyExists':
          setError('That user is already a member of this workspace.');
          return false;
        case 'TeamUserNotFound':
          setError('That user does not exist in Choco.');
          return false;
        default:
          setError(
            (result && 'message' in result ? result.message : null) ??
              'The member could not be added.'
          );
          return false;
      }
    },
    [add]
  );

  const assignRoles = useCallback(
    async ({
      workspaceId,
      userId,
      assignments,
    }: AssignRolesInput): Promise<boolean> => {
      setError(null);

      const { data } = await assign({
        variables: { input: { workspaceId, userId, assignments } },
      });
      const result = data?.teamMemberSetLocationAssignments;

      switch (result?.__typename) {
        case 'TeamMemberSetLocationAssignmentsResult':
          await onChanged();
          return true;
        case 'TeamLocationRoleTypeMismatch':
          setError(
            `That role applies at a ${result.roleLocationType}, but the location is a ${result.locationType}.`
          );
          return false;
        case 'TeamMemberNotFound':
          setError('That user is not a member of this workspace yet.');
          return false;
        default:
          setError(
            (result && 'message' in result ? result.message : null) ??
              'The role could not be assigned.'
          );
          return false;
      }
    },
    [assign, onChanged]
  );

  const assignRole = useCallback(
    ({
      workspaceId,
      userId,
      locationId,
      roleId,
    }: AssignRoleInput): Promise<boolean> =>
      assignRoles({
        workspaceId,
        userId,
        assignments: [{ locationId, roleId }],
      }),
    [assignRoles]
  );

  const removeRole = useCallback(
    async ({
      workspaceId,
      userId,
      locationId,
      roleId,
      allowRemoveFromWorkspace = false,
    }: RemoveRoleInput): Promise<RemoveRoleOutcome> => {
      setError(null);

      const { data } = await remove({
        variables: {
          input: {
            workspaceId,
            userId,
            locationId,
            roleId,
            allowRemoveUserFromWorkspace: allowRemoveFromWorkspace,
          },
        },
      });
      const result = data?.teamMemberRemoveLocationAssignment;

      switch (result?.__typename) {
        case 'TeamMemberRemoveLocationAssignmentResult':
          await onChanged();
          return { removed: true, wouldRemoveFromWorkspace: false };
        // Not an error the user needs to see: it is the service asking whether
        // losing their last role — and so the workspace — is intended.
        case 'TeamMemberWouldBeRemovedFromWorkspace':
          return { removed: false, wouldRemoveFromWorkspace: true };
        default:
          setError(
            (result && 'message' in result ? result.message : null) ??
              'The role could not be removed.'
          );
          return { removed: false, wouldRemoveFromWorkspace: false };
      }
    },
    [remove, onChanged]
  );

  const revokeAll = useCallback(
    async ({ workspaceId, userId }: RevokeAllInput): Promise<boolean> => {
      setError(null);

      const { data } = await revoke({
        variables: { input: { workspaceId, userId } },
      });
      const result = data?.teamMemberRevokeAllWorkspaceAccess;

      if (result?.__typename === 'TeamMemberRevokeAllWorkspaceAccessResult') {
        await onChanged();
        return true;
      }

      setError(
        (result && 'message' in result ? result.message : null) ??
          'They could not be removed.'
      );
      return false;
    },
    [revoke, onChanged]
  );

  return {
    addMember,
    assignRole,
    assignRoles,
    removeRole,
    revokeAll,
    isWorking:
      addState.loading ||
      assignState.loading ||
      removeState.loading ||
      revokeState.loading,
    error,
    clearError: () => setError(null),
  };
};
